import React, { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { isEqual, sortBy, uniq } from 'lodash';
import { useIntl } from 'react-intl';
import { UseFormHandleSubmit } from 'react-hook-form';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';

import { ChartOfAccountBulkFlatItem } from '@futrli-sdk/consolidations';
import {
  UITreeNode,
  generateIndexFromTreeNodes,
  toastError,
  toastSuccess,
  toastDismiss,
  ToastId
} from '@futrli/ui-components';

import {
  ConsolidationConfig,
  CreateConsolidationParams,
  FxRateOverride,
  OffsetEliminationGroup
} from '../../core/api/consolidation/types';

import { CoaStructureTypes } from '../../core/types';

import { useDailyFxRatesOverrideForm } from './useDailyFxRatesOverrideForm/useDailyFxRatesOverrideForm';
import { EliminationsFormValues, useEliminationsForm } from './useEliminationsForm';
import { useConsolidationCRUD } from './use-consolidation-crud/use-consolidation-crud';
import { useSettingsForm } from './useSettingsForm';
import {
  convertFullCOAToTreeNodes,
  generateDailyFxRatesGridData,
  getStartEndGridDates,
  removeEliminationGroupFromList,
  removeEliminationGroupAccountFromList,
  updateEliminationGroup,
  getCOACategories,
  getEliminationsRequestData,
  addEliminationGroupAccountToList,
  generateUniqueEntityCode,
  sortCoaCategories,
  isMultiCurrency as isMultiCurrencyConfig,
  hasEnoughOrganisations
} from './utils';
import { COAAccount, COATreeNode, DailyFxRatesOverrideFormValues, ExtendedOrganisation } from './types';
import { ExtendedCurrency } from './use-consolidation-crud/types';
import { hasDuplicateValues } from '../consolidation-wizard-context/utils';
import { ORGS_MAX_LIMIT } from '../constants';
import { TRACKING_EVENTS, trackEvent } from '../../core/tracker';

// eslint-disable-next-line no-magic-numbers
const MAX_ORGS_WARNING_THRESHOLD = ORGS_MAX_LIMIT - 2;

interface ConsolidationContextProps {
  children?: ReactNode;
}

export interface ConsolidationContextValues {
  config?: ConsolidationConfig;
  coaStructure: {
    coaStructure: CoaStructureTypes;
    handleChangeCoaStructure: (structure: CoaStructureTypes) => void;
  };
  currencies?: ExtendedCurrency[];
  eliminations: {
    coa: {
      categories?: { id: string; label: string }[];
      index?: Record<string, COATreeNode>;
      tree?: COATreeNode[];
    };
    eliminationGroups: Record<string, OffsetEliminationGroup[]>;
    eliminationsForm: ReturnType<typeof useEliminationsForm>;
    handleRemoveAllEliminationGroups: () => void;
    handleAddEliminationGroupAccount: (eliminationGroup: OffsetEliminationGroup, accountId: string | number) => void;
    handleCloseEliminationsModal: () => void;
    handleCreateEliminationGroup: () => void;
    handleEditEliminationGroup: (eliminationGroup: OffsetEliminationGroup) => void;
    handleMoveEliminationGroupAccount: (eliminationGroup: OffsetEliminationGroup, accountId: string | number) => void;
    handleRemoveEliminationGroupAccount: (accountId: string | number) => void;
    handleRemoveEliminationGroup: (eliminationGroup: OffsetEliminationGroup) => void;
    handleSaveEliminationGroup: ReturnType<UseFormHandleSubmit<EliminationsFormValues>>;
    showEliminationsModal: boolean;
  };
  dailyFxRates: {
    fxRatesOverrideForm: ReturnType<typeof useDailyFxRatesOverrideForm>;
    gridData?: ReturnType<typeof generateDailyFxRatesGridData>;
    handleAddFxRateOverride: () => void;
    handleCancelFxRatesOverrideModal: () => void;
    handleChangeFxRatesCurrencyTab: (index: number) => void;
    handleDeleteFxRateOverride: (index: number) => void;
    handleEditFxRateOverrides: () => void;
    handleSaveFxRateOverrides: () => void;
    selectedFXCurrencyCode: string;
    showFxRatesOverrideModal: boolean;
  };
  flags: {
    isMultiCurrency: boolean;
    isReady: boolean;
    isSaving: boolean;
  };
  handleSubmit: () => Promise<ConsolidationConfig | undefined>;
  handleSubmitAndExit: () => Promise<void>;
  settings: {
    presentationCurrency: string;
    settingsForm: ReturnType<typeof useSettingsForm>;
    handleAddOrganisation: (id: string) => void;
    handleChangeCurrency: (input: ExtendedCurrency | null) => void;
    handleOrganisationCodeUpdate: (orgId: string, code: string) => void;
    handleRemoveOrganisation: (id: string) => void;
    isMaxOrgsLimitReached: boolean;
    organisations?: ExtendedOrganisation[];
    orgCodeMap: Record<string, string>;
    orgsWithEliminations: string[];
    selectedOrganisations: ExtendedOrganisation[];
    uniqueConsolidationCurrencyCodes: string[];
    uniqueEntitiyCurrencyCodes: string[];
    uniqueFxCurrencyCodes: string[];
    unselectedOrganisations: ExtendedOrganisation[];
  };
}

const context = createContext(({} as unknown) as ConsolidationContextValues);

export const ConsolidationContext = (props: ConsolidationContextProps) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const { organisationId: consolidationId } = useParams();
  const MODAL_RESET_DELAY_MS = 250;

  // Settings
  const [orgCodeMap, setOrgCodeMap] = useState<Record<string, string>>({});
  const [selectedOrganisations, setSelectedOrganisations] = useState<ExtendedOrganisation[]>([]);
  const [currencySettingsChanged, setCurrencySettingsChanged] = useState(false);
  const [presentationCurrency, setPresentationCurrency] = useState('');
  const orgLimitWarningToastId = useRef<ToastId>();
  const shortCodeToastId = useRef<ToastId>();
  const settingsForm = useSettingsForm();

  // Settings form
  const {
    formState: settingsFormState,
    getValues: settingsFormGetValues,
    restoreConfig: settingsFormRestoreConfig,
    trigger: settingsFormTrigger,
    watch: settingsFormWatch,
    setValue: settingsFormSetValue
  } = settingsForm;
  const [yearEnd] = settingsFormWatch(['yearEnd']);

  // Eliminations
  const [eliminationGroups, setEliminationGroups] = useState<Record<string, OffsetEliminationGroup[]>>({});
  const eliminationGroupToEdit = useRef<OffsetEliminationGroup>();
  const eliminationsForm = useEliminationsForm();
  const [showEliminationsModal, setShowEliminationsModal] = useState(false);

  // Eliminations form
  const {
    handleSubmit: eliminationsFormHandleSubmit,
    reset: eliminationsFormReset,
    setValue: eliminationsFormSetValue
  } = eliminationsForm;

  // Flags
  const [isSaving, setIsSaving] = useState(false);

  // Daily FX Rates
  const [fxRateOverrides, setFxRateOverrides] = useState<FxRateOverride[]>([]);
  const [overrideFormData, setOverrideFormData] = useState<DailyFxRatesOverrideFormValues>({ overrides: [] });
  const [selectedFXCurrencyCode, setSelectedFXCurrencyCode] = useState('');
  const [showFxRatesOverrideModal, setShowFxRatesOverrideModal] = useState(false);
  const fxRatesOverrideForm = useDailyFxRatesOverrideForm(selectedFXCurrencyCode, presentationCurrency || '');

  // Daily FX rates override form
  const {
    addNewOverride: fxRatesOverrideFormAddNewOverride,
    getValues: fxRatesOverrideFormGetValues,
    removeOverride: fxRatesOverrideFormRemoveOverride,
    restore: fxRatesOverrideFormRestore,
    restoreConfig: fxRatesOverrideFormRestoreConfig
  } = fxRatesOverrideForm;

  // COA Structure
  const [coaStructure, setCoaStructure] = useState<CoaStructureTypes>(CoaStructureTypes.GROUP_BY_ORG_AND_CUSTOM_GROUPS);

  const {
    coaReq,
    config,
    currenciesReq,
    fxRatesReq,
    isReady,
    organisationsReq,
    saveConsolidation,
    setFxConfig
  } = useConsolidationCRUD(consolidationId, selectedOrganisations);

  const { data: coaData } = coaReq;
  const { data: currencies } = currenciesReq;
  const { data: fxRates } = fxRatesReq;
  const { data: organisations } = organisationsReq;

  const isMaxOrgsLimitReached = selectedOrganisations.length === ORGS_MAX_LIMIT;

  // ************************
  // * Calculated variables *
  // ************************

  // All unique currency code lists / isMultiCurrency flag
  const [
    uniqueConsolidationCurrencyCodes,
    uniqueEntitiyCurrencyCodes,
    uniqueFxCurrencyCodes,
    isMultiCurrency
  ] = useMemo(() => {
    const entityCodes = uniq(selectedOrganisations.map(org => org.currency_code)).filter(code => !!code);
    const allCodes = uniq([...entityCodes, presentationCurrency]).filter(code => !!code);
    const fxCodes = entityCodes.filter(code => code !== presentationCurrency);
    const isMultiCurrency = isMultiCurrencyConfig(selectedOrganisations, presentationCurrency);
    return [allCodes, entityCodes, fxCodes, isMultiCurrency];
  }, [presentationCurrency, selectedOrganisations]);

  // COA tree/index and categories list
  const [coaTree, coaIndex, coaCategories] = useMemo(() => {
    if (coaData) {
      const coa: COAAccount[] = coaData.map(acc => ({
        ...acc,
        code: orgCodeMap[acc.organisation_id] || '???'
      }));

      const eliminatedAccountIds = Object.keys(eliminationGroups)
        .map(categoryName => eliminationGroups[categoryName])
        .flat()
        .map(elimination => elimination.accounts)
        .flat();

      const filteredTree = convertFullCOAToTreeNodes(coa, eliminatedAccountIds);
      const index = generateIndexFromTreeNodes(convertFullCOAToTreeNodes(coa));

      const coaTree = sortCoaCategories(filteredTree.concat());
      const coaCategories = sortCoaCategories(getCOACategories(coa));

      return [coaTree, index, coaCategories];
    }
    return [];
  }, [coaData, eliminationGroups, orgCodeMap]);

  // List of orgs with eliminations
  const lastOrgsWithEliminationsRef = useRef<string[]>([]);
  const orgsWithEliminations = useMemo(() => {
    if (coaIndex) {
      const newOrgsWithEliminations = uniq(
        Object.values(eliminationGroups)
          .flat()
          .map(elimination => elimination.accounts)
          .flat()
          .map(accId => coaIndex[accId] as UITreeNode<ChartOfAccountBulkFlatItem>)
          .filter(node => node)
          .map(node => node.extra!.organisation_id)
      );

      if (!isEqual(lastOrgsWithEliminationsRef.current, newOrgsWithEliminations)) {
        lastOrgsWithEliminationsRef.current = newOrgsWithEliminations;
        return newOrgsWithEliminations;
      }
      return lastOrgsWithEliminationsRef.current;
    }
    return [];
  }, [coaIndex, eliminationGroups]);

  // FX override grid data
  const gridData = useMemo(() => {
    const fxRateData = fxRates?.[selectedFXCurrencyCode];
    if (yearEnd && fxRateData) {
      const { startDate, endDate } = getStartEndGridDates(yearEnd);
      return generateDailyFxRatesGridData(startDate, endDate, fxRateData, overrideFormData);
    }
  }, [fxRates, overrideFormData, selectedFXCurrencyCode, yearEnd]);

  // Current Fx overrides (changes depending upon presentation currency / selected FX currency tab)
  const currentFxRateOverrides = useMemo(() => {
    return fxRateOverrides.filter(
      override => override.currencyFrom === selectedFXCurrencyCode && override.currencyTo === presentationCurrency
    );
  }, [fxRateOverrides, presentationCurrency, selectedFXCurrencyCode]);

  // Unselected organisations (basically the inverse of selected orgs)
  const unselectedOrganisations = useMemo(() => {
    const selectedIds = selectedOrganisations.map(org => org.id);
    return (organisations || []).filter(org => !selectedIds.includes(org.id) && org.can_be_added_to_consolidation);
  }, [organisations, selectedOrganisations]);

  // ********
  // * Refs *
  // ********

  const coaStructureRef = useRef(coaStructure);
  const configRef = useRef<ConsolidationConfig>();
  const currentFxRateOverridesRef = useRef(currentFxRateOverrides);
  const eliminationGroupsRef = useRef(eliminationGroups);
  const fxRateOverridesRef = useRef(fxRateOverrides);
  const orgCodeMapRef = useRef(orgCodeMap);
  const overridesFormDataRef = useRef(overrideFormData);
  const presentationCurrencyRef = useRef(presentationCurrency);
  const selectedFXCurrencyCodeRef = useRef('');
  const selectedOrganisationsRef = useRef(selectedOrganisations);
  const uniqueFxCurrencyCodesRef = useRef(uniqueFxCurrencyCodes);
  const unselectedOrganisationsRef = useRef(unselectedOrganisations);

  // Keep refs up to date
  coaStructureRef.current = coaStructure;
  configRef.current = config;
  currentFxRateOverridesRef.current = currentFxRateOverrides;
  eliminationGroupsRef.current = eliminationGroups;
  fxRateOverridesRef.current = fxRateOverrides;
  orgCodeMapRef.current = orgCodeMap;
  overridesFormDataRef.current = overrideFormData;
  presentationCurrencyRef.current = presentationCurrency;
  selectedFXCurrencyCodeRef.current = selectedFXCurrencyCode;
  selectedOrganisationsRef.current = selectedOrganisations;
  uniqueFxCurrencyCodesRef.current = uniqueFxCurrencyCodes;
  unselectedOrganisationsRef.current = unselectedOrganisations;

  // ************
  // * Handlers *
  // ************

  const handleAddEliminationGroupAccount = useCallback(
    (eliminationGroup: OffsetEliminationGroup, accountId: string | number) => {
      const newEliminations = addEliminationGroupAccountToList(
        eliminationGroupsRef.current,
        eliminationGroup,
        accountId
      );
      setEliminationGroups(newEliminations);
    },
    []
  );

  const handleRemoveAllEliminationGroups = useCallback(() => {
    setEliminationGroups({});
  }, []);

  const handleAddFxRateOverride = useCallback(() => {
    fxRatesOverrideFormAddNewOverride();
  }, [fxRatesOverrideFormAddNewOverride]);

  const handleAddOrganisation = useCallback((id: string) => {
    const addedOrganisation = unselectedOrganisationsRef.current?.find(org => org.id === id);
    if (addedOrganisation) {
      const newSelectedOrganisations = sortBy([...selectedOrganisationsRef.current, addedOrganisation], ['name']);
      setSelectedOrganisations(newSelectedOrganisations);

      setOrgCodeMap({
        ...orgCodeMapRef.current,
        [addedOrganisation.id]: generateUniqueEntityCode(addedOrganisation.name, Object.values(orgCodeMapRef.current))
      });
    }
    setCurrencySettingsChanged(true);
  }, []);

  const handleMoveEliminationGroupAccount = useCallback(
    (eliminationGroup: OffsetEliminationGroup, accountId: string | number) => {
      let newEliminations = removeEliminationGroupAccountFromList(eliminationGroupsRef.current, accountId);
      newEliminations = addEliminationGroupAccountToList(newEliminations, eliminationGroup, accountId);
      setEliminationGroups(newEliminations);
    },
    []
  );

  const handleOrganisationCodeUpdate = useCallback(
    (orgId: string, code: string) => {
      const newOrgCodeMap = { ...orgCodeMapRef.current, [orgId]: code };
      if (hasDuplicateValues(newOrgCodeMap) && !shortCodeToastId.current) {
        shortCodeToastId.current = toastError(
          intl.formatMessage({
            id: 'consolidation.same_short_code_detected',
            defaultMessage: 'Short code needs to be unique within consolidation.'
          }),
          {
            autoClose: false
          }
        );
      }
      setOrgCodeMap({ ...orgCodeMapRef.current, [orgId]: code });
    },
    [intl]
  );

  const handleRemoveOrganisation = useCallback((id: string) => {
    const removedOrganisation = selectedOrganisationsRef.current?.find(org => org.id === id);
    if (removedOrganisation) {
      const newSelectedOrgs = selectedOrganisationsRef.current.filter(org => org.id !== id);
      setSelectedOrganisations(newSelectedOrgs);
      setOrgCodeMap(Object.fromEntries(newSelectedOrgs.map(org => [org.id, orgCodeMapRef.current[org.id]])));
    }
    setCurrencySettingsChanged(true);
  }, []);

  const handleCloseEliminationsModal = useCallback(() => {
    setTimeout(eliminationsFormReset, MODAL_RESET_DELAY_MS);
    eliminationGroupToEdit.current = undefined;
    setShowEliminationsModal(false);
  }, [eliminationsFormReset]);

  const handleCancelFxRatesOverrideModal = useCallback(() => {
    setTimeout(() => {
      if (overridesFormDataRef.current) {
        fxRatesOverrideFormRestore(overridesFormDataRef.current);
      }
    }, MODAL_RESET_DELAY_MS);
    setShowFxRatesOverrideModal(false);
  }, [fxRatesOverrideFormRestore]);

  const handleCreateEliminationGroup = useCallback(() => {
    setShowEliminationsModal(true);
  }, []);

  const handleDeleteFxRateOverride = useCallback(
    (index: number) => {
      fxRatesOverrideFormRemoveOverride(index);
    },
    [fxRatesOverrideFormRemoveOverride]
  );

  const handleEditEliminationGroup = useCallback(
    (eliminationGroup: OffsetEliminationGroup) => {
      eliminationGroupToEdit.current = eliminationGroup;
      eliminationsFormSetValue('name', eliminationGroup.name);
      eliminationsFormSetValue('category', eliminationGroup.category);
      setShowEliminationsModal(true);
    },
    [eliminationsFormSetValue]
  );

  const handleEditFxRateOverrides = useCallback(() => {
    fxRatesOverrideFormRestore(overridesFormDataRef.current);
    setShowFxRatesOverrideModal(true);
  }, [fxRatesOverrideFormRestore]);

  const handleRemoveEliminationGroup = useCallback((eliminationGroup: OffsetEliminationGroup) => {
    const newEliminations = removeEliminationGroupFromList(eliminationGroupsRef.current, eliminationGroup);
    setEliminationGroups(newEliminations);
  }, []);

  const handleRemoveEliminationGroupAccount = useCallback((accountId: string | number) => {
    const newEliminations = removeEliminationGroupAccountFromList(eliminationGroupsRef.current, accountId);
    setEliminationGroups(newEliminations);
  }, []);

  const handleSaveEliminationGroup = useCallback(
    (formData: EliminationsFormValues) => {
      let newData: Record<string, OffsetEliminationGroup[]>;

      if (eliminationGroupToEdit.current) {
        newData = updateEliminationGroup(eliminationGroupsRef.current, eliminationGroupToEdit.current, formData);
      } else {
        newData = { ...eliminationGroupsRef.current };
        newData[formData.category] = [
          ...(newData[formData.category] || []),
          {
            ...formData,
            accounts: []
          }
        ];
      }

      setEliminationGroups(newData);
      trackEvent(TRACKING_EVENTS.CREATE_CONSOLIDATION_ELIMINATION);
      handleCloseEliminationsModal();
    },
    [eliminationGroupToEdit, handleCloseEliminationsModal]
  );

  const handleSaveFxRateOverrides = useCallback(() => {
    // Update stored fx override form data
    const formData = fxRatesOverrideFormGetValues();

    // Update state with new fx rates (only overwrite those applicable to the current display currency)
    const otherOverrides = fxRateOverridesRef.current.filter(
      o => o.currencyFrom !== selectedFXCurrencyCodeRef.current || o.currencyTo !== presentationCurrencyRef.current
    );
    setFxRateOverrides([...otherOverrides, ...formData.overrides]);

    trackEvent(TRACKING_EVENTS.EDIT_FX_RATES_CONSOLIDATION);
    setShowFxRatesOverrideModal(false);
  }, [fxRatesOverrideFormGetValues]);

  const handleChangeCoaStructure = useCallback((structure: CoaStructureTypes) => {
    setCoaStructure(structure);
  }, []);

  const handleChangeCurrency = useCallback(
    (newValue: ExtendedCurrency | null) => {
      setPresentationCurrency(newValue?.code || '');
      settingsFormSetValue('currency', newValue?.code || '');
      setCurrencySettingsChanged(true);
      settingsFormTrigger();
    },
    [settingsFormSetValue, settingsFormTrigger]
  );

  const handleChangeFxRatesCurrencyTab = useCallback((index: number) => {
    setSelectedFXCurrencyCode(uniqueFxCurrencyCodesRef.current[index] || '');
  }, []);

  const handleSubmit = useCallback(async () => {
    settingsFormTrigger();

    if (!settingsFormState.isValid || !hasEnoughOrganisations(selectedOrganisationsRef.current, presentationCurrency)) {
      throw new Error('Settings form is not in a valid state');
    }

    setIsSaving(true);

    const settingsFormData = settingsFormGetValues();

    const requestData: CreateConsolidationParams = {
      name: settingsFormData.name,
      financial_year_end_day: settingsFormData.yearEnd.getDate(),
      financial_year_end_month: settingsFormData.yearEnd.getMonth() + 1,
      display_currency: settingsFormData.currency,
      organisations: selectedOrganisationsRef.current.map(org => ({
        organisation_id: org.id,
        short_code: orgCodeMapRef.current[org.id] || ''
      })),
      eliminations: getEliminationsRequestData(eliminationGroupsRef.current),
      account_structure: coaStructureRef.current,
      fx_overrides: fxRateOverridesRef.current.map(override => ({
        currency_from: override.currencyFrom,
        currency_to: override.currencyTo,
        override_from: format(override.dateFrom, 'yyyy-MM-dd'),
        override_to: format(override.dateTo, 'yyyy-MM-dd'),
        rate: override.dailyRate
      }))
    };

    try {
      const isNewConsolidation = !configRef.current?.id;
      const response = await saveConsolidation(requestData);

      if (isNewConsolidation) {
        trackEvent(
          isMultiCurrency
            ? TRACKING_EVENTS.CREATE_CONSOLIDATION_MULTI_CURRENCY
            : TRACKING_EVENTS.CREATE_CONSOLIDATION_SINGLE_CURRENCY
        );
      }

      toastSuccess(
        intl.formatMessage({
          id: 'consolidation.consolidation_saved',
          defaultMessage: 'Consolidation saved'
        })
      );

      return response;
    } catch (e) {
      toastError(
        intl.formatMessage({
          id: 'consolidation.failed_to_save_consolidation',
          defaultMessage: 'Failed to save consolidation'
        })
      );
    } finally {
      setIsSaving(false);
    }
  }, [intl, isMultiCurrency, presentationCurrency, saveConsolidation, settingsFormGetValues, settingsFormState.isValid, settingsFormTrigger]);

  const handleSubmitAndExit = useCallback(async () => {
    await handleSubmit();
    navigate(-1);
  }, [handleSubmit, navigate]);

  // ***********
  // * Effects *
  // ***********

  // If config changes, update state/form values
  useEffect(() => {
    if (config) {
      settingsFormRestoreConfig(config);
      setPresentationCurrency(config.presentationCurrency);
      setEliminationGroups(config.eliminations);
      setCoaStructure(config.coaStructure);
      setFxRateOverrides(config.fxRateOverrides);
    }
  }, [config, settingsFormRestoreConfig]);

  // If config and orgs change, generate selected orgs list and org code map
  useEffect(() => {
    if (config && organisations) {
      const selectedOrgIds = config.organisations.map(org => org.id);
      const selectedOrgs = organisations.filter(org => selectedOrgIds.includes(org.id));
      setSelectedOrganisations(selectedOrgs);

      // Update short code map with saved codes
      const newOrgCodeMap = Object.fromEntries(config.organisations.map(org => [org.id, org.code]));
      setOrgCodeMap(newOrgCodeMap);
    }
  }, [config, organisations]);

  // If current FX rate overrides change, update overrides form
  useEffect(() => {
    setOverrideFormData(fxRatesOverrideFormRestoreConfig(currentFxRateOverrides));
  }, [currentFxRateOverrides, fxRatesOverrideFormRestoreConfig]);

  // If available FX currency codes change, ensure we have a valid currency code tab selected
  useEffect(() => {
    if (!selectedFXCurrencyCode || !uniqueFxCurrencyCodes.includes(selectedFXCurrencyCode)) {
      setSelectedFXCurrencyCode(uniqueFxCurrencyCodes[0] || '');
    }
  }, [selectedFXCurrencyCode, uniqueFxCurrencyCodes]);

  // If fields related to FX rates change, update the fx rate config
  useEffect(() => {
    if (isMultiCurrency) {
      setFxConfig({
        presentationCurrency,
        currencies: uniqueFxCurrencyCodes,
        yearEnd
      });
    }
  }, [isMultiCurrency, presentationCurrency, setFxConfig, uniqueFxCurrencyCodes, yearEnd]);

  // If currency settings change and is multi currency, show info toast (we only show this once)
  useEffect(() => {
    if (currencySettingsChanged && !orgLimitWarningToastId.current) {
      if (selectedOrganisations.length >= MAX_ORGS_WARNING_THRESHOLD) {
        orgLimitWarningToastId.current = toastSuccess(
          intl.formatMessage(
            {
              id: 'consolidation.max_org_limit_warning',
              defaultMessage:
                'The current limit for entities within a consolidation is {limit}, contact our support team if you would like to have this number increased.'
            },
            {
              limit: ORGS_MAX_LIMIT
            }
          ),
          {
            autoClose: false
          }
        );
      }
    }
  }, [currencySettingsChanged, intl, selectedOrganisations.length]);

  // Close multi-currency consolidation toast on unmount if shown
  useEffect(
    () => () => {
      if (orgLimitWarningToastId.current) {
        toastDismiss(orgLimitWarningToastId.current);
      }
      if (shortCodeToastId.current) {
        toastDismiss(shortCodeToastId.current);
      }
    },
    []
  );

  const values = {
    config,
    coaStructure: {
      coaStructure,
      handleChangeCoaStructure
    },
    currencies,
    dailyFxRates: {
      fxRatesOverrideForm,
      gridData,
      handleAddFxRateOverride,
      handleCancelFxRatesOverrideModal,
      handleChangeFxRatesCurrencyTab,
      handleDeleteFxRateOverride,
      handleEditFxRateOverrides,
      handleSaveFxRateOverrides,
      selectedFXCurrencyCode,
      showFxRatesOverrideModal
    },
    eliminations: {
      coa: {
        categories: coaCategories,
        index: coaIndex,
        tree: coaTree
      },
      eliminationGroups,
      eliminationsForm,
      handleAddEliminationGroupAccount,
      handleRemoveAllEliminationGroups,
      handleCloseEliminationsModal,
      handleCreateEliminationGroup,
      handleEditEliminationGroup,
      handleMoveEliminationGroupAccount,
      handleRemoveEliminationGroupAccount,
      handleRemoveEliminationGroup,
      handleSaveEliminationGroup: eliminationsFormHandleSubmit(handleSaveEliminationGroup),
      showEliminationsModal
    },
    flags: {
      isMultiCurrency,
      isReady,
      isSaving
    },
    handleSubmit,
    handleSubmitAndExit,
    settings: {
      presentationCurrency,
      settingsForm,
      handleAddOrganisation,
      handleChangeCurrency,
      handleOrganisationCodeUpdate,
      handleRemoveOrganisation,
      isMaxOrgsLimitReached,
      organisations,
      orgCodeMap,
      orgsWithEliminations,
      selectedOrganisations,
      uniqueConsolidationCurrencyCodes,
      uniqueEntitiyCurrencyCodes,
      uniqueFxCurrencyCodes,
      unselectedOrganisations
    }
  };

  return <context.Provider value={values}>{props.children}</context.Provider>;
};

export const useConsolidationContext = () => useContext(context);
